import { inject, injectable } from 'inversify';
import TrackRequest from '@framework/analytics-tracker/TrackRequest';
import { SenderFactory, SenderType } from '@framework/analytics-tracker/Sender';
import { SessionService } from '@users/session/SessionService';
import Logger from '@framework/sentry/Logger';
import { IDealroadshowTrackerPayload } from './vo/IDealroadshowTracker';
import { TriggerEvent } from '@/dealroadshow/application/AnalyticsTracker/TriggerEvent';
import Container from '@framework/di/Container';

class TrackingValidationError extends Error {
  constructor(
    public readonly invalidParams: string[],
    public readonly payload: Record<string, any>,
    public readonly headers: Record<string, any>,
  ) {
    super('Required tracking parameter/s is not specified');
  }
}

@injectable()
class DealroadshowTrackingManager {
  constructor(
    @inject(SenderFactory) private senderFactory: SenderFactory,
    @inject(Container) private container: Container,
  ) {}

  private requiredParams = ['viewSsid', 'roadshowId', 'fingerprint', 'viewerId'];

  private shouldValidate = (payload: IDealroadshowTrackerPayload) => {
    const isAllowedTriggerEvent = TriggerEvent.WINDOW_CLOSE === payload.triggerEvent;

    return !isAllowedTriggerEvent;
  };

  private validate = async (request: TrackRequest) => {
    return new Promise<void>((resolve, reject) => {
      const { payload, headers } = request.params;

      if (!this.shouldValidate(payload)) {
        resolve();
        return;
      }

      const params = { ...payload, ...headers };
      const invalidParams = this.requiredParams.filter((key) => !params[key]);

      if (invalidParams.length === 0) {
        resolve();
        return;
      }

      reject(new TrackingValidationError(invalidParams, payload, headers));
    });
  };

  async track({
    eventName,
    payload,
    isBeacon = false,
  }: {
    eventName: string,
    payload?: IDealroadshowTrackerPayload,
    isBeacon?: boolean,
  }) {
    const senderType = isBeacon ? SenderType.Beacon : SenderType.Socket;
    const request = new TrackRequest(eventName, payload);

    try {
      await this.validate(request);

      return await this.senderFactory.create(senderType).track({ eventName, payload });
    } catch (error) {
      if (error instanceof TrackingValidationError) {
        Logger.error({
          message: error.message,
          error,
          context: {
            payload: error.payload,
            headers: error.headers,
            invalidParams: error.invalidParams,
          },
        });
      }

      const sessionService = this.container.get<SessionService>(SessionService);
      await sessionService.logout({
        redirect: true,
        error: true,
      });

      return Promise.resolve();
    }
  }
}

export default DealroadshowTrackingManager;
